import { ApiFetcherArgs } from "@ts-rest/core";
import { initQueryClient } from "@ts-rest/react-query";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError, Method } from "axios";

import { AccessTokenSingleton } from "#modules/auth/domain/AccessTokenSingleton";
import { isUserDisabledError } from "#modules/auth/domain/isUserDisabledError";
import { handleDisabledUser } from "#modules/auth/infra/handleDisabledUser";
import {
  businessContract,
  coreContract,
  optimizationsContract,
  quoteRequestLinesContract,
  quoteRequestsContract,
  quoteRequestsV2Contract,
  searchContract,
} from "shared";

export const contract = {
  ...coreContract,
  ...businessContract,
  optimizations: optimizationsContract,
  quoteRequests: quoteRequestsContract,
  quoteRequestsV2: quoteRequestsV2Contract,
  quoteRequestLines: quoteRequestLinesContract,
  search: searchContract,
};

export const api = initQueryClient(contract, {
  baseUrl: import.meta.env.VITE_BASE_API_URL,
  baseHeaders: {},
  jsonQuery: true,
  api: async ({
    method,
    path,
    headers,
    body,
    responseType,
  }: ApiFetcherArgs & {
    timeout?: number;
    responseType?: AxiosRequestConfig["responseType"];
  }) => {
    const accessToken = AccessTokenSingleton.getInstance().getAccessToken();

    try {
      const result = await axios.request({
        method: method as Method,
        url: path,
        headers: {
          ...headers,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          "x-page-url": window.location.href,
        },
        data: body,
        responseType,
      });

      return {
        status: result.status,
        body: result.data,
        headers: result.headers as unknown as Headers,
      };
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        const error = e as AxiosError;
        const response = error.response as AxiosResponse;

        if (isUserDisabledError(response.status, response.data)) {
          void handleDisabledUser();
        }

        return {
          status: response.status,
          body: response.data,
          headers: response.headers as unknown as Headers,
        };
      }
      throw e;
    }
  },
});
